import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiGet, apiPost, apiPut, apiDelete } from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatPrice, relativeTime } from "../lib/format";
import { categoryLabel, conditionLabel } from "../lib/categories";
import { Button, Textarea, Alert, EmptyState } from "../components/ui";
import { ReportListing } from "../components/ReportListing";

export function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState(0); // active photo index

  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiGet(`/listings/${id}`)
      .then((data) => {
        if (!cancelled) setListing(data.listing);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="animate-pulse max-w-3xl mx-auto">
        <div className="aspect-video bg-slate-200 rounded-xl" />
        <div className="h-6 bg-slate-200 rounded mt-4 w-2/3" />
        <div className="h-4 bg-slate-100 rounded mt-3 w-1/3" />
      </div>
    );
  }
  if (error || !listing) {
    return (
      <EmptyState title="Listing not found">
        {error || "This item may have been removed."}{" "}
        <Link to="/gear" className="text-brand-600 underline">
          Back to browse
        </Link>
      </EmptyState>
    );
  }

  const isOwner = user && listing.sellerId === user.userId;
  // An admin who isn't the seller still gets a takedown control — but keeps the
  // message form, since they may well want to contact the seller instead.
  const canModerate = user && user.isAdmin && !isOwner;
  const photos = listing.photos || [];
  const cover = photos[active];

  async function sendMessage(e) {
    e.preventDefault();
    if (!message.trim()) return;
    setSendError("");
    setSending(true);
    try {
      const { conversationId } = await apiPost("/conversations", {
        listingId: listing.id,
        body: message.trim(),
      });
      navigate(`/gear/messages/${conversationId}`);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  }

  async function toggleSold() {
    setWorking(true);
    try {
      const next = listing.status === "sold" ? "active" : "sold";
      const { listing: updated } = await apiPut(`/listings/${listing.id}`, { status: next });
      setListing(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this listing? This can't be undone.")) return;
    setWorking(true);
    try {
      await apiDelete(`/listings/${listing.id}`);
      navigate("/gear/my-listings");
    } catch (err) {
      setError(err.message);
      setWorking(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-700 mb-3">
        ← Back
      </button>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square bg-slate-100 rounded-xl overflow-hidden">
            {cover ? (
              <img src={cover} alt={listing.title} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-7xl opacity-40">
                ⚽
              </div>
            )}
            {listing.status === "sold" && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <span className="text-white font-bold text-2xl border-2 border-white px-4 py-1.5 rounded rotate-[-8deg]">
                  SOLD
                </span>
              </div>
            )}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 mt-2 overflow-x-auto">
              {photos.map((p, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`h-16 w-16 shrink-0 rounded-lg overflow-hidden border-2 ${
                    i === active ? "border-brand-500" : "border-transparent"
                  }`}
                >
                  <img src={p} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <span className="inline-block text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
            {categoryLabel(listing.category)}
          </span>
          <h1 className="text-2xl font-bold text-slate-800 mt-2">{listing.title}</h1>
          <div className="text-3xl font-extrabold text-brand-700 mt-1">{formatPrice(listing.price)}</div>

          <dl className="mt-4 grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-slate-400">Condition</dt>
            <dd className="text-slate-700 font-medium">{conditionLabel(listing.condition)}</dd>
            {listing.size && (
              <>
                <dt className="text-slate-400">Size</dt>
                <dd className="text-slate-700 font-medium">{listing.size}</dd>
              </>
            )}
            <dt className="text-slate-400">Seller</dt>
            <dd className="text-slate-700 font-medium">{listing.sellerName}</dd>
            <dt className="text-slate-400">Listed</dt>
            <dd className="text-slate-700 font-medium">{relativeTime(listing.createdAt)}</dd>
          </dl>

          {listing.description && (
            <p className="mt-4 text-slate-600 whitespace-pre-wrap leading-relaxed">{listing.description}</p>
          )}

          {/* Owner controls */}
          {isOwner ? (
            <div className="mt-6 space-y-2">
              <div className="rounded-lg bg-brand-50 border border-brand-100 text-brand-700 text-sm px-3 py-2">
                This is your listing.
              </div>
              <div className="flex gap-2">
                <Button onClick={() => navigate(`/gear/listing/${listing.id}/edit`)} disabled={working} className="flex-1">
                  Edit
                </Button>
                <Button variant="secondary" onClick={toggleSold} disabled={working}>
                  {listing.status === "sold" ? "Mark available" : "Mark sold"}
                </Button>
                <Button variant="danger" onClick={remove} disabled={working}>
                  Delete
                </Button>
              </div>
            </div>
          ) : (
            <>
              {canModerate && (
                <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
                  <p className="text-sm font-semibold text-amber-900">Admin</p>
                  <p className="mt-0.5 text-sm text-amber-800">
                    You can remove this listing on behalf of SSA. This can't be undone.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/gear/listing/${listing.id}/edit`)}
                      disabled={working}
                    >
                      Edit
                    </Button>
                    <Button variant="danger" onClick={remove} disabled={working}>
                      Remove listing
                    </Button>
                  </div>
                </div>
              )}
              <form onSubmit={sendMessage} className="mt-6">
                <h2 className="font-semibold text-slate-700 mb-2">Message {listing.sellerName}</h2>
                <Textarea
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Hi! Is the "${listing.title}" still available?`}
                />
                {sendError && (
                  <div className="mt-2">
                    <Alert>{sendError}</Alert>
                  </div>
                )}
                <Button type="submit" disabled={sending || !message.trim()} className="w-full mt-2">
                  {sending ? "Sending…" : "Send message"}
                </Button>
              </form>
              <ReportListing listingId={listing.id} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
